import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import {
  FaMapMarkerAlt, FaStar, FaArrowLeft, FaUsers, FaChild, FaCheck, FaCalendarAlt, FaBed,
  FaWifi, FaSwimmingPool, FaUtensils, FaDumbbell, FaSnowflake, FaCar, FaCoffee, FaLock
} from "react-icons/fa";
import { MdSpa, MdOutlineBalcony, MdKitchen, MdHotTub, MdTv, MdRoomService, MdBathtub } from "react-icons/md";
import axios from "axios";
import { baseurl, imgurl } from "../../baseurl";
import "./HotelFullDetails.css";

const ICON_MAP = {
  wifi: <FaWifi />, pool: <FaSwimmingPool />, restaurant: <FaUtensils />,
  fitness_center: <FaDumbbell />, spa: <MdSpa />, ac_unit: <FaSnowflake />,
  local_parking: <FaCar />, balcony: <MdOutlineBalcony />, kitchen: <MdKitchen />,
  hot_tub: <MdHotTub />, tv: <MdTv />, coffee_maker: <FaCoffee />,
  room_service: <MdRoomService />, bathtub: <MdBathtub />, lock: <FaLock />
};

const HotelFullDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const roomsRef = useRef(null);
  const { uuid } = useParams();

  const { hotel_id, checkIn, checkOut, rooms, adults, children } = location.state || {};

  const searchDetails = {
    checkIn: checkIn || new Date().toISOString().split('T')[0],
    checkOut: checkOut || new Date(Date.now() + 86400000).toISOString().split('T')[0],
    rooms: rooms || 1,
    adults: adults || 2,
    children: children || 0,
    hotel_id,
  };

  const [hotel, setHotel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeImg, setActiveImg] = useState(0);
  const [isLocking, setIsLocking] = useState(false);
  const [hotelMarkup, setHotelMarkup] = useState({ type: "flat", amount: 0 });
  const [sessionId] = useState(`sess_${Math.random().toString(36).substr(2, 9)}`);

  const calculateCorrectPrice = (room) => {
    if (!room) return 0;
    let basePrice = 0;
    if (room.weekly_prices) {
      const days = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
      const dayName = days[new Date(searchDetails.checkIn).getDay()];
      basePrice = parseFloat(room.weekly_prices[dayName] || room.base_price || 0);
    } else {
      basePrice = parseFloat(room.base_price || 0);
    }

    let finalPrice = basePrice;
    const markupAmount = parseFloat(hotelMarkup.amount);
    if (hotelMarkup.type === "percent") {
      finalPrice = basePrice + (basePrice * (markupAmount / 100));
    } else {
      finalPrice = basePrice + markupAmount;
    }
    return finalPrice;
  };

  const fetchHotelData = async () => {
    try {
      const markupRes = await axios.post(`${baseurl}/getServiceMarkupsGuest`);
      const hotelService = markupRes.data.markups?.find(m => Number(m.service_id) === 1);
      if (hotelService) {
        setHotelMarkup({ type: hotelService.markup_type, amount: hotelService.amount });
      }
      const res = await axios.get(`${baseurl}/getHotelFullDetails/${uuid}`, {
        params: { check_in: searchDetails.checkIn, check_out: searchDetails.checkOut }
      });
      if (res.data.status === "success") setHotel(res.data.data);
    } catch (err) {
      console.error("Fetch Error:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (uuid) fetchHotelData(); }, [uuid]);

  const handleSelectRoom = async (room) => {
    if (room.available_inventory <= 0) {
      alert("Sorry, this room type just sold out.");
      return;
    }
    setIsLocking(true);
    try {
      const res = await axios.post(`${baseurl}/lockRoom`, {
        room_type_id: room.room_id,
        session_id: sessionId,
        check_in: searchDetails.checkIn,
        check_out: searchDetails.checkOut,
      });

      if (res.data.status === "success") {
        const priceToUse = calculateCorrectPrice(room);
        navigate("/pre-checkout", {
          state: { 
            hotel, 
            room: { ...room, displayPrice: priceToUse }, 
            sessionId, 
            searchDetails, 
            hotel_id: hotel.id 
          }
        });
      }
    } catch {
      alert("Could not lock room. Please try again.");
    } finally {
      setIsLocking(false);
    }
  };

  if (loading) return <div className="go-loader">Checking availability...</div>;
  if (!hotel) return <div className="go-wrapper">Hotel not found.</div>;

  const nights = Math.max(1, Math.round((new Date(searchDetails.checkOut) - new Date(searchDetails.checkIn)) / 86400000));

  return (
    <div className="go-wrapper">
      {/* Navigation */}
      <nav className="go-nav-simple">
        <div className="go-container">
          <button onClick={() => navigate(-1)} className="go-back-link">
            <FaArrowLeft /> Back to Results
          </button>
        </div>
      </nav>

      {/* Main Container */}
      <div className="go-container">
        {/* Hotel Header */}
        <header className="go-hotel-header">
          <div className="go-header-left">
            <div className="go-rating-badges">
              <span className="go-star-pill">
                {hotel.star_rating} <FaStar />
              </span>
              <span className="go-type-pill">{hotel.hotel_type}</span>
            </div>
            <h1>{hotel.hotel_name}</h1>
            <p className="go-location">
              <FaMapMarkerAlt /> {hotel.hotel_address}, {hotel.city_village}
            </p>
          </div>
          <div className="go-header-right">
            <p className="go-starting-price">
              Rooms from 
              <span>₹{calculateCorrectPrice(hotel.all_rooms[0])?.toLocaleString('en-IN')}</span>
            </p>
          </div>
        </header>

        {/* Gallery Section */}
        <section className="go-gallery">
          <div className="go-gallery-main">
            <img 
              src={`${imgurl}/${hotel.images[activeImg]}`} 
              alt="Hotel" 
              loading="lazy"
            />
          </div>
          <div className="go-gallery-grid">
            {hotel.images.slice(0, 4).map((img, i) => (
              <div 
                key={i} 
                className={`go-thumb ${activeImg === i ? 'active' : ''}`} 
                onClick={() => setActiveImg(i)}
                role="button"
                tabIndex={0}
                onKeyPress={(e) => e.key === 'Enter' && setActiveImg(i)}
              >
                <img 
                  src={`${imgurl}/${img}`} 
                  alt={`Gallery thumbnail ${i + 1}`}
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </section>

        {/* Rooms Section */}
        <section className="go-rooms-area" ref={roomsRef}>
          <h2 className="go-section-title">Available Rooms</h2>
          <div className="go-room-list">
            {hotel.all_rooms?.map((room) => {
              const isSoldOut = room.available_inventory <= 0;
              const currentPrice = calculateCorrectPrice(room);
              return (
                <div key={room.room_id} className="go-room-full-card">
                  {/* Room Image */}
                  <div className="go-room-img">
                    <img 
                      src={`${imgurl}/${room.images[0]}`} 
                      alt={room.name}
                      loading="lazy"
                    />
                  </div>

                  {/* Room Details */}
                  <div className="go-room-details">
                    <h4>{room.name}</h4>
                    <div className="go-room-occ">
                      <span>
                        <FaUsers /> {room.max_adults} Adults
                      </span>
                    </div>
                  </div>

                  {/* Price & Action */}
                  <div className="go-room-price-action">
                    <div className="go-price-tag">
                      <h3>₹{currentPrice?.toLocaleString('en-IN')}</h3>
                      <small>+ taxes & fees</small>
                    </div>
                    <button
                      disabled={isSoldOut || isLocking}
                      onClick={() => handleSelectRoom(room)}
                      className={isSoldOut ? "go-btn-disabled" : "go-select-btn"}
                      aria-label={isSoldOut ? "Sold Out" : `Book ${room.name}`}
                    >
                      {isLocking ? "Securing..." : isSoldOut ? "Sold Out" : "Book Now"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </div>
    </div>
  );
};

export default HotelFullDetails;